import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, Bookmark, LogOut, User, Shield, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/Logo";
import ListingCard from "@/components/ListingCard";
import PartCard from "@/components/PartCard";
import AdSlot from "@/components/AdSlot";
import { api, formatApiError, absUrl } from "@/lib/api";
import { toast } from "sonner";
import {
    MAKES,
    ALL_MODELS,
    PERFORMANCE_PACKAGES,
    DRIVETRAINS,
    TRANSMISSIONS,
    MODS_LEVELS,
    SELLER_TYPES,
    SORT_OPTIONS,
    CURRENT_YEAR,
    MAX_LISTING_YEAR,
    MIN_LISTING_YEAR,
} from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import Seo, { itemListSchema } from "@/components/Seo";

const DEFAULT_FILTERS = {
    make: "",
    model: "",
    trim: "",
    engine: "",
    drivetrain: "",
    transmission: "",
    seller_type: "",
    mods_level: "",
    min_price: "",
    max_price: "",
    min_year: "",
    max_year: "",
    max_mileage: "",
    search: "",
    sort: "recent",
};

const QUICK_MODELS = [
    { label: "All", model: "" },
    { label: "Charger", model: "Charger" },
    { label: "Challenger", model: "Challenger" },
    { label: "300", model: "300" },
    { label: "TRX", model: "1500 TRX" },
    { label: "RHO", model: "1500 RHO" },
];

const MARKET_TABS = [
    { value: "dealer", label: "Dealer", testid: "market-tab-dealer" },
    { value: "private", label: "Private", testid: "market-tab-private" },
    { value: "parts", label: "Parts", testid: "market-tab-parts" },
];

function activeFilterCount(f) {
    return Object.entries(f).filter(([k, v]) => v && k !== "sort" && k !== "search").length;
}

export default function Marketplace() {
    const [tab, setTab] = useState(() => {
        try {
            const p = new URLSearchParams(window.location.search);
            const t = p.get("tab");
            return ["dealer", "private", "parts"].includes(t) ? t : "private";
        } catch {
            return "private";
        }
    });
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sheetOpen, setSheetOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => {
                if (v !== "" && v !== null && v !== undefined) params[k] = v;
            });
            if (tab === "parts") {
                const { data } = await api.get("/parts", { params: { limit: 40, search: params.search || undefined } });
                setItems(data.items);
                setTotal(data.total);
            } else {
                params.seller_type = tab; // "dealer" or "private"
                const { data } = await api.get("/listings", { params });
                setItems(data.items);
                setTotal(data.total);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [filters, tab]);

    useEffect(() => {
        load();
    }, [load]);

    async function saveCurrentSearch() {
        if (!user) return navigate("/login");
        const name = window.prompt("Name this search (e.g., 'Hellcat Widebody under $65k')");
        if (!name) return;
        try {
            await api.post("/saved-searches", { name, filters });
            toast.success("Search saved");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const filterCount = activeFilterCount(filters);
    const filterForm = (
        <FilterForm tab={tab}
            filters={filters}
            setFilters={setFilters}
            total={total}
            onClear={() => setFilters(DEFAULT_FILTERS)}
            onApply={() => setSheetOpen(false)}
        />
    );

    return (
        <div className="min-h-screen bg-onyx-900">
            <Seo
                title="Marketplace — Buy & Sell Dodge Hellcat, Challenger SRT, Charger SRT, TRX, Demon & Scat Pack"
                description="Browse the SRT Finder marketplace of Dodge SRT Hellcat, Redeye, Widebody, Scat Pack, SRT Demon, Chrysler 300 SRT8, and RAM TRX & RHO listings. Filter by make, model, trim, year, mileage, price, drivetrain, transmission, mods level, and location. Real market pricing. SRT / R/T only — no V6s."
                canonicalPath="/marketplace"
                jsonLd={itemListSchema(items)}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Marketplace", path: "/marketplace" },
                ]}
            />

            {/* Top bar */}
            <header
                className="sticky top-0 z-30 border-b border-onyx-600"
                style={{
                    background: "rgba(10,10,10,0.85)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                }}
            >
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Logo size="sm" />
                    <div className="flex-1 hidden md:flex items-center gap-2 pill-input py-2 max-w-lg mx-auto">
                        <Search className="w-4 h-4 text-onyx-500" />
                        <input
                            data-testid="marketplace-search-desktop"
                            value={filters.search}
                            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                            placeholder="Search Hellcat, Redeye, TRX, Widebody, Demon..."
                            className="w-full bg-transparent outline-none placeholder:text-onyx-500 text-sm"
                        />
                    </div>
                    <div className="flex-1 md:flex-none" />
                    <button
                        onClick={saveCurrentSearch}
                        data-testid="save-search-btn"
                        className="hidden lg:inline-flex btn-ghost px-3 py-2 items-center gap-2 text-xs font-bold"
                    >
                        <Bookmark className="w-4 h-4" /> Save
                    </button>
                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    data-testid="user-menu-trigger"
                                    className="w-9 h-9 rounded-full surface-elevated overflow-hidden flex items-center justify-center"
                                >
                                    {user.avatar_url ? (
                                        <img
                                            src={absUrl(user.avatar_url)}
                                            alt="me"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <User className="w-4 h-4 text-onyx-500" />
                                    )}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="surface border-onyx-600 min-w-[180px]"
                            >
                                <DropdownMenuLabel className="text-white">
                                    {user.name}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate("/garage")}>
                                    <User className="w-4 h-4 mr-2" /> Garage
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate("/favorites")}>
                                    <Bookmark className="w-4 h-4 mr-2" /> Saved
                                </DropdownMenuItem>
                                {user.role === "admin" && (
                                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                                        <Shield className="w-4 h-4 mr-2" /> Admin
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={async () => {
                                        await logout();
                                        navigate("/");
                                    }}
                                    data-testid="logout-btn"
                                >
                                    <LogOut className="w-4 h-4 mr-2" /> Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <Link
                            to="/login"
                            className="btn-red px-4 py-2 text-xs font-bold"
                            data-testid="header-signin"
                        >
                            Sign in
                        </Link>
                    )}
                </div>

                {/* Mobile search + filters */}
                <div className="max-w-7xl mx-auto px-4 pb-3 md:hidden flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-2 pill-input py-2">
                        <Search className="w-4 h-4 text-onyx-500" />
                        <input
                            data-testid="marketplace-search"
                            value={filters.search}
                            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                            placeholder="Search Hellcat, TRX, Widebody..."
                            className="w-full bg-transparent outline-none placeholder:text-onyx-500 text-sm"
                        />
                    </div>
                    <button
                        onClick={() => setSheetOpen(true)}
                        data-testid="open-filters-btn"
                        className="btn-ghost px-4 py-2.5 inline-flex items-center gap-2 text-xs font-bold"
                    >
                        <SlidersHorizontal className="w-4 h-4" /> Filters
                        {filterCount > 0 && (
                            <span
                                className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                                style={{ background: "#E10600", color: "#fff" }}
                            >
                                {filterCount}
                            </span>
                        )}
                    </button>
                </div>

                {/* Quick model chips */}
                <div className="max-w-7xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto no-scrollbar">
                    {QUICK_MODELS.map((qm) => (
                        <button
                            key={qm.label}
                            onClick={() => setFilters((f) => ({ ...f, model: qm.model }))}
                            className={`chip ${filters.model === qm.model ? "chip-active" : ""}`}
                            data-testid={`quick-${qm.label.toLowerCase()}`}
                        >
                            {qm.label}
                        </button>
                    ))}
                </div>
            </header>

            {/* Marketplace section tabs — Dealer / Private / Parts (sharp, no pills) */}
            <div className="max-w-7xl mx-auto px-4 mt-3">
                <div
                    role="tablist"
                    aria-label="Marketplace section"
                    className="inline-flex p-1 gap-1"
                    style={{
                        background: "rgba(20,20,20,0.6)",
                        border: "1px solid #262626",
                        borderRadius: 6,
                        backdropFilter: "blur(12px)",
                    }}
                >
                    {MARKET_TABS.map((t) => {
                        const active = tab === t.value;
                        return (
                            <button
                                key={t.value}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                data-testid={t.testid}
                                onClick={() => {
                                    setTab(t.value);
                                    const p = new URLSearchParams(window.location.search);
                                    p.set("tab", t.value);
                                    window.history.replaceState({}, "", `${window.location.pathname}?${p}`);
                                }}
                                className="px-4 py-1.5 text-sm font-bold transition-colors"
                                style={{
                                    borderRadius: 4,
                                    background: active ? "#E10600" : "transparent",
                                    color: active ? "#fff" : "rgba(255,255,255,0.7)",
                                    letterSpacing: "0.04em",
                                }}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Main content — CarGurus-style sidebar + Facebook-style feed */}
            <div className="max-w-7xl mx-auto px-4 py-5 flex gap-6">
                {/* Sidebar filters (desktop only) */}
                <aside
                    className="hidden md:block w-64 shrink-0 sticky self-start"
                    style={{ top: 168 }}
                    aria-label="Marketplace filters"
                >
                    <div className="surface p-5">
                        <div className="flex items-center justify-between mb-3">
                            <h2
                                className="text-white text-lg"
                                style={{
                                    fontFamily: "'Alfa Slab One', 'Ultra', serif",
                                    fontWeight: 400,
                                }}
                            >
                                Filters
                            </h2>
                            {filterCount > 0 && (
                                <button
                                    onClick={() => setFilters(DEFAULT_FILTERS)}
                                    className="text-xs text-hellcat font-bold label-caps"
                                    data-testid="sidebar-clear"
                                >
                                    Clear ({filterCount})
                                </button>
                            )}
                        </div>
                        <FilterForm tab={tab}
                            filters={filters}
                            setFilters={setFilters}
                            total={total}
                            compact
                        />
                    </div>
                    <div className="mt-4">
                        <AdSlot placement="marketplace-sidebar" variant="sidebar" />
                    </div>
                </aside>

                {/* Feed */}
                <main className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h1
                            className="text-white text-xl sm:text-2xl"
                            style={{
                                fontFamily: "'Alfa Slab One', 'Ultra', serif",
                                fontWeight: 400,
                            }}
                        >
                            {loading ? "Loading…" : `${total} ${total === 1 ? "listing" : "listings"}`}
                        </h1>
                        <select
                            data-testid="sort-select"
                            value={filters.sort}
                            onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
                            className="pill-input py-2 text-xs font-semibold"
                            style={{ paddingRight: 32 }}
                        >
                            {SORT_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="surface overflow-hidden">
                                    <div className="w-full aspect-[16/10] bg-onyx-800 animate-pulse" />
                                    <div className="p-4 space-y-3">
                                        <div className="h-4 bg-onyx-800 rounded animate-pulse w-2/3" />
                                        <div className="h-3 bg-onyx-800 rounded animate-pulse w-1/2" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-16 surface p-8">
                            <h2
                                className="text-white text-2xl"
                                style={{
                                    fontFamily: "'Alfa Slab One', 'Ultra', serif",
                                    fontWeight: 400,
                                }}
                            >
                                No SRT or R/T listings match
                            </h2>
                            <div className="text-sm text-onyx-500 mt-2 max-w-md mx-auto">
                                Try clearing filters or widening your search. New Hellcat, Redeye,
                                Scat Pack, TRX, and Demon listings drop on SRT Finder daily.
                            </div>
                            <button
                                onClick={() => setFilters(DEFAULT_FILTERS)}
                                className="btn-ghost px-5 py-2.5 mt-6 text-sm font-bold"
                                data-testid="clear-filters-btn"
                            >
                                Clear filters
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                            <AnimatePresence>
                                {items.map((l, i) => {
                                    const cards = [];
                                    // Inject an inline ad every 6th slot
                                    if (i > 0 && i % 6 === 0) {
                                        cards.push(
                                            <motion.div
                                                key={`ad-${i}`}
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                            >
                                                <AdSlot placement="marketplace-inline" />
                                            </motion.div>
                                        );
                                    }
                                    cards.push(
                                        <motion.div
                                            key={l.listing_id || l.part_id}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: Math.min(i * 0.03, 0.3) }}
                                        >
                                            {tab === "parts" ? <PartCard part={l} /> : <ListingCard listing={l} />}
                                        </motion.div>
                                    );
                                    return cards;
                                })}
                            </AnimatePresence>
                        </div>
                    )}
                </main>
            </div>

            {/* Mobile filter sheet */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetContent
                    side="bottom"
                    className="bg-onyx-800 border-onyx-600 max-h-[85vh] overflow-y-auto"
                    style={{ borderTopLeftRadius: 24, borderTopRightRadius: 24 }}
                    data-testid="filters-sheet"
                >
                    <SheetHeader>
                        <SheetTitle
                            className="text-white text-2xl text-left"
                            style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                        >
                            Filters
                        </SheetTitle>
                    </SheetHeader>
                    <div className="mt-4">
                        <FilterForm tab={tab}
                            filters={filters}
                            setFilters={setFilters}
                            total={total}
                            onClear={() => setFilters(DEFAULT_FILTERS)}
                            onApply={() => setSheetOpen(false)}
                        />
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}

// ---------- FilterForm (used inline in sidebar + inside sheet) ----------
function FilterForm({ filters, setFilters, total, onClear, onApply, compact = false, tab = "private" }) {
    return (
        <div className="flex flex-col gap-4">
            <Group label="Make">
                <select
                    data-testid="filter-make"
                    value={filters.make}
                    onChange={(e) =>
                        setFilters((f) => ({ ...f, make: e.target.value, model: "" }))
                    }
                    className="field-input"
                >
                    <option value="">Any</option>
                    {MAKES.map((m) => (
                        <option key={m} value={m}>
                            {m}
                        </option>
                    ))}
                </select>
            </Group>
            <Group label="Model">
                <select
                    data-testid="filter-model"
                    value={filters.model}
                    onChange={(e) => setFilters((f) => ({ ...f, model: e.target.value }))}
                    className="field-input"
                >
                    <option value="">Any</option>
                    {ALL_MODELS.map((m) => (
                        <option key={m} value={m}>
                            {m}
                        </option>
                    ))}
                </select>
            </Group>
            <Group label="Package / Trim">
                <select
                    data-testid="filter-trim"
                    value={filters.trim}
                    onChange={(e) => setFilters((f) => ({ ...f, trim: e.target.value }))}
                    className="field-input"
                >
                    <option value="">Any</option>
                    {PERFORMANCE_PACKAGES.map((m) => (
                        <option key={m} value={m}>
                            {m}
                        </option>
                    ))}
                </select>
            </Group>
            <div className={compact ? "grid grid-cols-2 gap-3" : "grid grid-cols-2 gap-3"}>
                <Group label="Min $">
                    <input
                        data-testid="filter-min-price"
                        type="number"
                        inputMode="numeric"
                        placeholder="Any"
                        min={0}
                        value={filters.min_price}
                        onChange={(e) =>
                            setFilters((f) => ({ ...f, min_price: e.target.value }))
                        }
                        className="field-input"
                    />
                </Group>
                <Group label="Max $">
                    <input
                        data-testid="filter-max-price"
                        type="number"
                        inputMode="numeric"
                        placeholder="Any"
                        min={0}
                        value={filters.max_price}
                        onChange={(e) =>
                            setFilters((f) => ({ ...f, max_price: e.target.value }))
                        }
                        className="field-input"
                    />
                </Group>
                <Group label="Min year">
                    <input
                        data-testid="filter-min-year"
                        type="number"
                        inputMode="numeric"
                        placeholder={String(MIN_LISTING_YEAR)}
                        min={MIN_LISTING_YEAR}
                        max={MAX_LISTING_YEAR}
                        value={filters.min_year}
                        onChange={(e) =>
                            setFilters((f) => ({ ...f, min_year: e.target.value }))
                        }
                        className="field-input"
                    />
                </Group>
                <Group label={`Max year (${MAX_LISTING_YEAR})`}>
                    <input
                        data-testid="filter-max-year"
                        type="number"
                        inputMode="numeric"
                        placeholder={String(MAX_LISTING_YEAR)}
                        min={MIN_LISTING_YEAR}
                        max={MAX_LISTING_YEAR}
                        value={filters.max_year}
                        onChange={(e) =>
                            setFilters((f) => ({ ...f, max_year: e.target.value }))
                        }
                        className="field-input"
                    />
                </Group>
                <Group label="Max miles" className="col-span-2">
                    <input
                        data-testid="filter-max-mileage"
                        type="number"
                        inputMode="numeric"
                        placeholder="Any"
                        min={0}
                        value={filters.max_mileage}
                        onChange={(e) =>
                            setFilters((f) => ({ ...f, max_mileage: e.target.value }))
                        }
                        className="field-input"
                    />
                </Group>
            </div>
            <Group label="Drivetrain">
                <select
                    data-testid="filter-drivetrain"
                    value={filters.drivetrain}
                    onChange={(e) => setFilters((f) => ({ ...f, drivetrain: e.target.value }))}
                    className="field-input"
                >
                    <option value="">Any</option>
                    {DRIVETRAINS.map((m) => (
                        <option key={m} value={m}>
                            {m}
                        </option>
                    ))}
                </select>
            </Group>
            <Group label="Transmission">
                <select
                    data-testid="filter-transmission"
                    value={filters.transmission}
                    onChange={(e) =>
                        setFilters((f) => ({ ...f, transmission: e.target.value }))
                    }
                    className="field-input"
                >
                    <option value="">Any</option>
                    {TRANSMISSIONS.map((m) => (
                        <option key={m} value={m}>
                            {m}
                        </option>
                    ))}
                </select>
            </Group>
            <Group label="Mods">
                <select
                    data-testid="filter-mods"
                    value={filters.mods_level}
                    onChange={(e) => setFilters((f) => ({ ...f, mods_level: e.target.value }))}
                    className="field-input"
                >
                    <option value="">Any</option>
                    {MODS_LEVELS.map((m) => (
                        <option key={m} value={m}>
                            {m}
                        </option>
                    ))}
                </select>
            </Group>
            <Group label="Seller">
                <input
                    type="text"
                    disabled
                    value={tab === "parts" ? "Parts (all sellers)" : tab === "dealer" ? "Dealer only" : "Private only"}
                    className="field-input opacity-70"
                    data-testid="filter-seller-type"
                />
            </Group>

            {(onClear || onApply) && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                    {onClear && (
                        <button
                            type="button"
                            onClick={onClear}
                            data-testid="sheet-clear-filters"
                            className="btn-ghost py-3 font-bold text-sm"
                        >
                            Clear all
                        </button>
                    )}
                    {onApply && (
                        <button
                            type="button"
                            onClick={onApply}
                            data-testid="sheet-apply-filters"
                            className="btn-red py-3 font-bold text-sm"
                        >
                            Show {total} results
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function Group({ label, children, className = "" }) {
    return (
        <label className={`flex flex-col gap-1.5 ${className}`}>
            <span className="label-caps">{label}</span>
            {children}
        </label>
    );
}
